import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lock, ArrowRight, ArrowLeft, ClipboardList, UploadCloud, MailCheck, RefreshCw, KeyRound } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { companyTypes } from "../mock";
import PasswordStrength from "../components/PasswordStrength";
import PasswordInput from "../components/PasswordInput";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import ThemeToggle from "../components/ThemeToggle";

const portalMeta = {
  admin: { title: "Admin Console", desc: "Manage users, permissions, settings and firm-wide controls.", icon: Lock, color: "#2f2b8f", tint: "bg-indigo-50 text-indigo-700", canRegister: true },
  employee: { title: "Request Center", desc: "Create, manage and track document requests from clients.", icon: ClipboardList, color: "#2f2b8f", tint: "bg-indigo-50 text-indigo-700", canRegister: false },
  client: { title: "Client Workspace", desc: "A secure space to upload documents and respond to requests.", icon: UploadCloud, color: "#059669", tint: "bg-emerald-50 text-emerald-700", canRegister: false },
};

const redirectFor = (role) => (role === "admin" ? "/portal" : role === "client" ? "/client" : "/employee");

const AuthPage = () => {
  const { portal } = useParams();
  const meta = portalMeta[portal] || portalMeta.admin;
  const navigate = useNavigate();
  const { login, register, sendLoginCode, loginWithCode } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState("login");
  const [method, setMethod] = useState(meta.canRegister ? "password" : "code"); // invitees default to code
  const [codeStep, setCodeStep] = useState("email"); // email | code
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", firm_name: "", year_of_incorporation: "", company_type: "", admin_name: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const Icon = meta.icon;

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        if (!form.company_type) { toast({ title: "Please select company type", variant: "destructive" }); setBusy(false); return; }
        if (form.password.length < 8) { toast({ title: "Password too short", description: "Use at least 8 characters.", variant: "destructive" }); setBusy(false); return; }
        const user = await register({
          firm_name: form.firm_name, year_of_incorporation: form.year_of_incorporation,
          company_type: form.company_type, admin_name: form.admin_name,
          email: form.email, password: form.password,
        });
        toast({ title: `Welcome, ${user.name}!`, description: "Verify your email to activate your account." });
        navigate("/verify-email");
      } else {
        const user = await login(form.email, form.password);
        if (user.email_verified === false) {
          toast({ title: "Verify your email", description: "Please confirm the code we sent you." });
          navigate("/verify-email");
        } else {
          toast({ title: `Welcome back, ${user.name}!` });
          navigate(redirectFor(user.role));
        }
      }
    } catch (err) {
      toast({ title: "Authentication failed", description: err?.response?.data?.detail || "Please check your details.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const requestCode = async (e) => {
    e && e.preventDefault();
    if (!form.email) { toast({ title: "Enter your email", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await sendLoginCode(form.email);
      setCodeStep("code"); setOtp(""); setCooldown(30);
      toast({ title: "Code sent", description: `If an account exists, a 6-digit code was emailed to ${form.email}` });
    } catch (err) {
      toast({ title: "Could not send code", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const verifyCode = async (value) => {
    const c = value || otp;
    if (c.length !== 6) return;
    setBusy(true);
    try {
      const res = await loginWithCode(form.email, c);
      toast({ title: "Signed in" });
      if (res.needs_password_setup) navigate("/set-password");
      else navigate(redirectFor(res.user.role));
    } catch (err) {
      toast({ title: "Sign-in failed", description: err?.response?.data?.detail || "Invalid code.", variant: "destructive" });
      setOtp("");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50/60">
      <div className="flex w-full flex-col justify-center px-6 py-12">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8 flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
              <ArrowLeft className="h-4 w-4" /> Back to home
            </Link>
            <ThemeToggle />
          </div>
          <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm">
            <div className={`mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${meta.tint}`}>
              <Icon className="h-7 w-7" strokeWidth={1.8} />
            </div>
            <h1 className="text-2xl font-bold text-[#1a1a2e]">{meta.title}</h1>
            <p className="mt-1 text-sm text-slate-500">{meta.desc}</p>

            {meta.canRegister && (
              <div className="mt-6 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
                <button onClick={() => setMode("login")} className={`rounded-lg py-2 text-sm font-semibold transition-colors ${mode === "login" ? "bg-white text-[#1a1a2e] shadow-sm" : "text-slate-500"}`}>Sign in</button>
                <button onClick={() => setMode("register")} className={`rounded-lg py-2 text-sm font-semibold transition-colors ${mode === "register" ? "bg-white text-[#1a1a2e] shadow-sm" : "text-slate-500"}`}>Register firm</button>
              </div>
            )}

            {/* Register form (admin) */}
            {mode === "register" ? (
              <form onSubmit={submit} className="mt-6 space-y-3">
                <div className="space-y-1.5"><Label>Your name</Label><Input required value={form.admin_name} onChange={set("admin_name")} placeholder="Jane Doe" className="rounded-xl" /></div>
                <div className="space-y-1.5"><Label>Name of firm</Label><Input required value={form.firm_name} onChange={set("firm_name")} placeholder="Doe & Associates" className="rounded-xl" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Year of formation</Label><Input required value={form.year_of_incorporation} onChange={set("year_of_incorporation")} placeholder="2015" className="rounded-xl" /></div>
                  <div className="space-y-1.5">
                    <Label>Type of company</Label>
                    <Select value={form.company_type} onValueChange={(v) => setForm({ ...form, company_type: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{companyTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Email</Label><Input required type="email" value={form.email} onChange={set("email")} placeholder="you@firm.co" className="rounded-xl" /></div>
                <div className="space-y-1.5"><Label>Password</Label><PasswordInput required value={form.password} onChange={set("password")} placeholder="••••••••" data-testid="auth-password" /><PasswordStrength password={form.password} /></div>
                <Button type="submit" disabled={busy} className="group w-full rounded-xl py-6 text-sm font-semibold text-white" style={{ backgroundColor: meta.color }}>
                  {busy ? "Please wait..." : "Create firm account"}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </form>
            ) : method === "code" ? (
              /* Passwordless code sign-in */
              codeStep === "email" ? (
                <form onSubmit={requestCode} className="mt-6 space-y-3">
                  <div className="space-y-1.5"><Label>Email</Label><Input data-testid="code-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@firm.co" className="rounded-xl" /></div>
                  <Button data-testid="send-code-btn" type="submit" disabled={busy} className="group w-full rounded-xl py-6 text-sm font-semibold text-white" style={{ backgroundColor: meta.color }}>
                    <MailCheck className="mr-2 h-4 w-4" /> {busy ? "Sending..." : "Email me a sign-in code"}
                  </Button>
                  <button type="button" data-testid="use-password-link" onClick={() => setMethod("password")} className="mx-auto flex items-center gap-1 text-xs font-medium text-[#2f2b8f] hover:underline"><Lock className="h-3.5 w-3.5" /> Sign in with your password instead</button>
                </form>
              ) : (
                <div className="mt-6">
                  <p className="text-sm text-slate-500">Enter the 6-digit code we emailed to <span className="font-semibold text-[#1a1a2e]">{form.email}</span>.</p>
                  <div className="mt-5 flex justify-center">
                    <InputOTP maxLength={6} value={otp} onChange={(v) => { setOtp(v); if (v.length === 6) verifyCode(v); }}>
                      <InputOTPGroup>
                        {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button data-testid="verify-code-btn" onClick={() => verifyCode()} disabled={busy || otp.length !== 6} className="group mt-6 w-full rounded-xl py-6 text-sm font-semibold text-white" style={{ backgroundColor: meta.color }}>
                    {busy ? "Signing in..." : "Sign in"}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <div className="mt-4 text-center text-sm text-slate-500">
                    Didn't get it?{" "}
                    <button disabled={cooldown > 0} onClick={() => requestCode()} className="inline-flex items-center gap-1 font-medium text-[#2f2b8f] hover:underline disabled:opacity-50">
                      <RefreshCw className="h-3.5 w-3.5" /> {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                    </button>
                  </div>
                  <button onClick={() => { setCodeStep("email"); setOtp(""); }} className="mx-auto mt-4 block text-xs font-medium text-slate-400 hover:text-slate-600">Use a different email</button>
                </div>
              )
            ) : (
              /* Password sign-in */
              <form onSubmit={submit} className="mt-6 space-y-3">
                <div className="space-y-1.5"><Label>Email</Label><Input required type="email" value={form.email} onChange={set("email")} placeholder="you@firm.co" className="rounded-xl" /></div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>Password</Label>
                    <button type="button" onClick={() => setForgotOpen(true)} className="text-xs font-medium text-[#2f2b8f] hover:underline">Forgot password?</button>
                  </div>
                  <PasswordInput required value={form.password} onChange={set("password")} placeholder="••••••••" data-testid="auth-password" />
                </div>
                <Button type="submit" disabled={busy} className="group w-full rounded-xl py-6 text-sm font-semibold text-white" style={{ backgroundColor: meta.color }}>
                  {busy ? "Please wait..." : "Sign in"}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <button type="button" onClick={() => { setMethod("code"); setCodeStep("email"); }} className="mx-auto flex items-center gap-1 text-xs font-medium text-[#2f2b8f] hover:underline"><KeyRound className="h-3.5 w-3.5" /> Sign in with an email code instead</button>
              </form>
            )}

            {mode === "login" && !meta.canRegister && (
              <p className="mt-5 text-center text-xs text-slate-400">
                {portal === "client" ? "Client Workspace" : "Request Center"} accounts are created by your firm. Sign in with your email — no password needed to start.
              </p>
            )}
          </div>
        </div>
      </div>
      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
};

export default AuthPage;
